"use client";

import { useState, useRef, useEffect, useContext, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { MessagesContext } from "../MessagesContext";
import { LoginContext } from "../LoginContext";
import { apiService } from "../backend/api";
import { useTranslation } from "react-i18next";
import {
  X,
  Send,
  MessagesSquare,
  History,
  MessageSquarePlus,
  Bot,
  User,
  ChevronDown,
  Circle,
  Clock,
  CheckCircle
} from "lucide-react";
import { io } from "socket.io-client";
import { getBaseUrl } from "../config/apiConfig";

const categoryOptions = [
  { value: "scan_issues", label: "Hochlade Problem" },
  { value: "technical_issues", label: "Modell-Viewer Problem" },
  { value: "payment_issues", label: "Pakete Problem" },
  { value: "general_support", label: "Sonstiges" },
];

const HelpChatButton = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { chatMessages, addMessage, chatCategory: contextCategory, activeLiveChat, setActiveLiveChat, updateUnreadCount, updateNewCount, setCategory: setContextCategory } = useContext(MessagesContext);
  const { loginInfo } = useContext(LoginContext);
  const username = loginInfo?.username;
  const isAdmin = loginInfo?.user?.roles?.some(r => (typeof r === 'string' ? r : r.name) === 'admin' || (typeof r === 'string' ? r : r.name) === 'support');

  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState("general_support");
  const [showCategorySheet, setShowCategorySheet] = useState(false);
  const [message, setMessage] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [isSupportOnline, setIsSupportOnline] = useState(false);
  const [socket, setSocket] = useState(null);
  const [liveStarted, setLiveStarted] = useState(() => {
    return !!sessionStorage.getItem("live_chat_started");
  });

  // Internal conversationId state
  const [internalConversationId, setInternalConversationId] = useState(() => {
    const saved = sessionStorage.getItem("live_chat_id");
    if (saved) return saved;
    const newId = username ? `live_${username}` : `live_guest_${Math.random().toString(36).substring(7)}`;
    sessionStorage.setItem("live_chat_id", newId);
    return newId;
  });

  // Effective conversationId (can be overridden by activeLiveChat from context for admin)
  const conversationId = (isAdmin && activeLiveChat) ? activeLiveChat.roomId : internalConversationId;

  const [liveChatMessages, setLiveChatMessages] = useState([]);
  const messagesEndRef = useRef(null);
  const inactivityTimerRef = useRef(null);
  const liveMessagesRef = useRef([]);
  const pendingMessageRef = useRef(null);

  useEffect(() => {
    liveMessagesRef.current = liveChatMessages;
  }, [liveChatMessages]);

  // Auto-open when admin joins a chat from dashboard
  useEffect(() => {
    if (isAdmin && activeLiveChat) {
      setOpen(true);
      if (activeLiveChat.category) {
        setCategory(activeLiveChat.category);
        setContextCategory(activeLiveChat.category);
      }
      setLiveChatMessages([]); // Clear previous live session messages when switching
    }
  }, [isAdmin, activeLiveChat]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [chatMessages, liveChatMessages, open]);

  // Check support availability
  useEffect(() => {
    const checkStatus = async () => {
      try {
        const status = await apiService.checkSupportOnline();
        setIsSupportOnline(status.isOnline);
      } catch (error) {
        console.error("Error checking support status:", error);
      }
    };

    checkStatus();
    const interval = setInterval(checkStatus, 30000); // Check every 30s
    return () => clearInterval(interval);
  }, []);

  // Auto-set liveStarted if there are messages (handles refresh)
  useEffect(() => {
    if (liveChatMessages.length > 0 && !liveStarted) {
      setLiveStarted(true);
    }
  }, [liveChatMessages.length, liveStarted]);

  // Socket initialization
  useEffect(() => {
    // Only connect if box is open AND support is online AND (we are admin OR user has started the chat)
    const shouldConnect = open && isSupportOnline && (isAdmin || liveStarted);

    if (shouldConnect) {
      // console.log("🔌 Initializing socket connection...");
      const newSocket = io(getBaseUrl());
      setSocket(newSocket);

      newSocket.emit("join-conversation", conversationId);

      // If we are a user (not admin), announce ourselves to support
      if (isAdmin) {
        newSocket.emit("admin-join-support");
      } else {
        newSocket.emit("announce-live-chat", {
          conversationId,
          username: username || "Guest",
          category: contextCategory || category,
          timestamp: new Date()
        });
      }

      newSocket.on("chat-history", (history) => {
        setLiveChatMessages(history.map(msg => ({
          id: Date.now() + Math.random(),
          sender: msg.sender,
          content: msg.content,
          timestamp: msg.timestamp,
          isLive: true,
          isSystem: msg.isSystem
        })));
      });

      newSocket.on("new-message", (msg) => {
        setLiveChatMessages(prev => [...prev, {
          id: Date.now() + Math.random(),
          sender: msg.sender,
          content: msg.content,
          timestamp: msg.timestamp,
          isLive: true,
          isSystem: msg.isSystem
        }]);
      });

      newSocket.on("chat-ended", () => {
        // Clear history and reset the ID so they must start a new chat
        if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
        setLiveChatMessages([]);
        setLiveStarted(false);
        const newId = `live_${Date.now()}`;
        setInternalConversationId(newId);
        sessionStorage.setItem("live_chat_id", newId);
        if (!isAdmin) {
          // Add a system message to inform the customer
          setLiveChatMessages([{
            id: Date.now(),
            sender: "System",
            content: t("helpChat.sessionEnded"),
            timestamp: new Date(),
            isSystem: true
          }]);
        }
      });

      return () => {
        if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
        newSocket.disconnect();
        setSocket(null);
      };
    }
  }, [open, isSupportOnline, conversationId, liveStarted, isAdmin, username]);

  // Sync category changes without reconnecting
  useEffect(() => {
    if (socket && !isAdmin && liveStarted) {
      socket.emit("announce-live-chat", {
        conversationId,
        username: username || "Guest",
        category: contextCategory || category,
        timestamp: new Date(),
        isUpdate: true // Server could use this to know it's just a category change
      });
    }
  }, [socket, contextCategory, category, conversationId, isAdmin, liveStarted, username]);

  // Flush pending message when socket is ready
  useEffect(() => {
    if (socket && pendingMessageRef.current) {
      // console.log("🚀 Flushing pending message...");
      socket.emit("chat-message", {
        conversationId,
        sender: username || "User",
        content: pendingMessageRef.current,
        timestamp: new Date()
      });
      pendingMessageRef.current = null;
    }
  }, [socket, conversationId, username]);

  const resetInactivityTimer = useCallback(() => {
    if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);

    // Only set timer if there's an active live chat and we are NOT an admin
    // Or if we want to include admin inactivity too? User said "if customer didnot anser"
    if (isSupportOnline && liveChatMessages.length > 0) {
      inactivityTimerRef.current = setTimeout(() => {
        // console.log("⏱️ Inactivity timeout reached (2 min). Closing chat...");
        handleEndChat(true);
      }, 120000); // 2 minutes
    }
  }, [isSupportOnline, liveChatMessages.length]);

  // Reset timer on message change
  useEffect(() => {
    if (liveChatMessages.length > 0) {
      resetInactivityTimer();
    }
  }, [liveChatMessages, resetInactivityTimer]);

  const isLoggedIn = () => {
    return !!username;
  };

  const handleSendMessage = async () => {
    if (!message.trim()) return;

    const usernameToUse = username || "User";

    if (isSupportOnline) {
      // Live Mode
      if (!liveStarted && !isAdmin) {
        // console.log("🎬 Starting live chat for user...");
        pendingMessageRef.current = message;
        setLiveStarted(true);
        sessionStorage.setItem("live_chat_started", "true");
        setMessage("");
        return;
      }

      if (socket) {
        socket.emit("chat-message", {
          conversationId,
          sender: usernameToUse,
          content: message,
          timestamp: new Date()
        });
        setMessage("");
        return;
      } else if (liveStarted) {
        // If live is started but socket is temporarily null (reconnecting)
        pendingMessageRef.current = message;
        setMessage("");
        return;
      }
    }

    // Offline Mode (existing logic)
    const newMessage = addMessage(message, category);

    setIsTyping(true);

    // Send to DB
    try {
      await apiService.request(`${getBaseUrl()}/api/messages/create`, {
        method: "POST",
        body: JSON.stringify({
          senderUsername: usernameToUse,
          senderType: "user",
          senderName: usernameToUse,
          recipientUsername: "admin",
          recipientType: "admin",
          subject: `${t("helpChat.supportRequest")}: ${
            categoryOptions.find((opt) => opt.value === category)?.label ||
            category
          }`,
          content: message,
          category: category,
          conversationId: newMessage.conversationId,
        }),
      });

      setTimeout(() => {
        updateUnreadCount();
        updateNewCount();
      }, 1000);
    } catch (error) {
      console.error("Error saving message to database:", error);
    }

    // Automated Reply
    setTimeout(() => {
      addMessage(
        t("messages.automated_reply", "Danke für Ihre Nachricht! Unser Team wird sich so schnell wie möglich bei Ihnen melden."),
        category,
        "support",
        "Support"
      );
      setIsTyping(false);
    }, 2000);

    setMessage("");
  };

  const handleEndChat = useCallback(async (shouldCloseTicket = false) => {
    if (liveChatMessages.length === 0) {
      if (isAdmin && activeLiveChat) setActiveLiveChat(null);
      setOpen(false);
      return;
    }

    try {
      const usernameToUse = username || "User";
      const messagesToArchive = liveMessagesRef.current;
      const fullContent = messagesToArchive.map(m => `[${new Date(m.timestamp).toLocaleTimeString()}] ${m.sender}: ${m.content}`).join('\n');

      await apiService.request(`${getBaseUrl()}/api/messages/create`, {
        method: "POST",
        body: JSON.stringify({
          senderUsername: usernameToUse,
          senderType: isAdmin ? "admin" : "user",
          senderName: usernameToUse,
          recipientUsername: isAdmin ? (activeLiveChat?.username || "user") : "admin",
          recipientType: isAdmin ? "user" : "admin",
          subject: `Live Chat: ${conversationId}`,
          content: `Vollständiges Chat-Protokoll:\n\n${fullContent}`,
          category: "general_support",
          conversationId: conversationId,
        }),
      });

      // If admin wants to close the ticket immediately
      if (isAdmin && shouldCloseTicket) {
        try {
          await apiService.closeConversation(conversationId);
        } catch (closeErr) {
          console.error("Error closing conversation:", closeErr);
        }
      } else if (!isAdmin && shouldCloseTicket) {
        try {
          await apiService.closeConversation(conversationId);
        } catch (closeErr) {
          console.error("Error auto-closing conversation:", closeErr);
        }
      }

      if (socket) {
        socket.emit("end-live-chat", { conversationId });
      }

      setLiveChatMessages([]);
      if (isAdmin && activeLiveChat) {
        setActiveLiveChat(null);
      } else {
        const newId = `live_${Date.now()}`;
        setInternalConversationId(newId);
        sessionStorage.setItem("live_chat_id", newId);
      }

      setOpen(false);

      setTimeout(() => {
        updateUnreadCount();
        updateNewCount();
      }, 1000);
    } catch (error) {
      console.error("Error ending chat:", error);
      setOpen(false);
    }
  }, [liveChatMessages.length, isAdmin, activeLiveChat, username, conversationId, socket, updateUnreadCount, updateNewCount, setActiveLiveChat, t]);

  const handleNewChat = () => {
    setCategory("general_support");
    setMessage("");
  };

  const handleNavigateToMessages = () => {
    if (isLoggedIn()) {
      navigate("/profile/messages");
    } else {
      navigate("/login");
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const unreadCount = chatMessages.length > 0 ? 1 : 0; // Simplified for demo

  // Listen for toggle event from Header (mobile chat button)
  useEffect(() => {
    const handler = () => setOpen(prev => !prev);
    window.addEventListener('toggle-help-chat', handler);
    return () => window.removeEventListener('toggle-help-chat', handler);
  }, []);

  return (
    <>
      {/* Chat Window */}
      {open && (
        <div className="fixed inset-0 top-[5rem] sm:top-auto sm:bottom-24 sm:right-10 sm:left-auto sm:w-[380px] sm:h-[600px] sm:max-h-[600px] sm:rounded-3xl z-[9998] overflow-hidden flex flex-col border-0 sm:border sm:border-white/20 sm:dark:border-slate-700/50 bg-white dark:bg-slate-900 sm:shadow-2xl animate-in fade-in sm:zoom-in duration-200">
          {/* Header */}
          <div className="px-4 py-3.5 sm:p-5 bg-gradient-to-br from-[#0f7699] to-[#4c9fb9] dark:from-[#0a5f7a] dark:to-[#0f7699] text-white flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-white/20 flex items-center justify-center backdrop-blur-md">
                <MessagesSquare size={18} />
              </div>
              <div>
                <h3 className="text-sm font-black leading-tight">
                  {isSupportOnline ? t("helpChat.liveSupport") : t("helpChat.offlineMessage")}
                </h3>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <Circle size={8} className={`fill-current ${isSupportOnline ? "text-emerald-400" : "text-amber-400"}`} />
                  <p className="text-[10px] font-bold opacity-80 uppercase tracking-widest leading-none">
                    {isSupportOnline ? t("helpChat.online") : t("helpChat.weWillReplyShortly")}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-1">
              <button
                onClick={handleNewChat}
                className="w-9 h-9 rounded-xl flex items-center justify-center hover:bg-white/10 transition-colors"
                title={t("helpChat.newChat")}
              >
                <MessageSquarePlus size={18} />
              </button>
              <button
                onClick={isSupportOnline ? handleEndChat : () => setOpen(false)}
                className="w-9 h-9 rounded-xl flex items-center justify-center hover:bg-white/10 transition-colors"
              >
                <X size={18} />
              </button>
            </div>
          </div>

          {/* History Button - Disabled during active live chat */}
          {!isAdmin && (
            <div className="px-4 py-3 sm:p-4 border-b border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/50">
              <button
                onClick={handleNavigateToMessages}
                disabled={liveChatMessages.length > 0 && isSupportOnline}
                className={`w-full py-2.5 sm:py-3 px-4 rounded-xl text-xs font-black flex items-center justify-center gap-2 border transition-all uppercase tracking-widest ${
                  liveChatMessages.length > 0 && isSupportOnline
                  ? "bg-slate-100 dark:bg-slate-800 text-slate-300 dark:text-slate-600 cursor-not-allowed border-slate-100 dark:border-slate-800"
                  : "bg-white dark:bg-slate-800 text-[#0f7699] dark:text-[#4fc3f7] border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-750 active:scale-[0.98]"
                }`}
              >
                <History size={14} />
                {t("helpChat.previousConversations")}
              </button>
            </div>
          )}

          {/* Messages Area */}
          <div className="flex-1 p-4 sm:p-5 overflow-y-auto bg-slate-50 dark:bg-slate-950 flex flex-col gap-3 sm:gap-4 scrollbar-thin">
            {!isSupportOnline && chatMessages.length === 0 && (
              <div className="h-full flex flex-col items-center justify-center text-center p-6 gap-4">
                <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl sm:rounded-3xl bg-amber-50 dark:bg-amber-900/20 flex items-center justify-center text-amber-500 dark:text-amber-400 border border-amber-100 dark:border-amber-800/30">
                  <Clock size={28} />
                </div>
                <div className="space-y-2">
                  <h4 className="text-sm font-black text-slate-900 dark:text-slate-100 uppercase tracking-tight">{t("helpChat.supportOffline")}</h4>
                  <p className="text-[11px] font-bold text-slate-400 dark:text-slate-500 leading-relaxed uppercase tracking-tighter">
                    {t("helpChat.leaveMessageWeWillContact")}
                  </p>
                </div>
              </div>
            )}

            {isSupportOnline && liveChatMessages.length === 0 && (
              <div className="h-full flex flex-col items-center justify-center text-center p-6 gap-4">
                <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl sm:rounded-3xl bg-blue-50 dark:bg-[#0f7699]/20 flex items-center justify-center text-[#0f7699] dark:text-[#4fc3f7] border border-blue-100 dark:border-[#0f7699]/30">
                  <MessagesSquare size={28} />
                </div>
                <div className="space-y-2">
                  <h4 className="text-sm font-black text-slate-900 dark:text-slate-100 uppercase tracking-tight">{t("helpChat.liveChatReady")}</h4>
                  <p className="text-[11px] font-bold text-slate-400 dark:text-slate-500 leading-relaxed uppercase tracking-tighter">
                    {t("helpChat.writeUsStaffWillReply")}
                  </p>
                </div>
              </div>
            )}

            {/* Render Live or Offline Messages */}
            {(isSupportOnline ? liveChatMessages : chatMessages).map((msg, index) => (
              <div
                key={msg.id || index}
                className={`max-w-[85%] flex flex-col gap-1 animate-in fade-in slide-in-from-bottom-2 duration-300 ${
                  msg.sender === (username || "User") ? "self-end items-end" : "self-start items-start"
                }`}
              >
                <div className={`px-4 py-3 rounded-2xl text-sm font-medium ${
                  msg.sender === (username || "User")
                    ? "bg-gradient-to-br from-[#0f7699] to-[#4c9fb9] text-white rounded-tr-sm"
                    : "bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 rounded-tl-sm"
                }`}>
                  {msg.content}
                </div>
                <span className="text-[9px] font-black text-slate-400 dark:text-slate-600 uppercase tracking-widest px-2">
                  {msg.timestamp ? new Date(msg.timestamp).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : t("helpChat.justNow")}
                </span>
              </div>
            ))}

            {isTyping && (
              <div className="self-start flex items-center gap-3 animate-in fade-in duration-300">
                <div className="w-8 h-8 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center text-slate-500 dark:text-slate-400">
                  <MessagesSquare size={14} />
                </div>
                <div className="px-4 py-3 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl rounded-tl-sm">
                  <div className="flex gap-1.5 items-center h-4">
                    <div className="w-1.5 h-1.5 bg-slate-400 dark:bg-slate-500 rounded-full animate-bounce [animation-delay:-0.3s]" />
                    <div className="w-1.5 h-1.5 bg-slate-400 dark:bg-slate-500 rounded-full animate-bounce [animation-delay:-0.15s]" />
                    <div className="w-1.5 h-1.5 bg-slate-400 dark:bg-slate-500 rounded-full animate-bounce" />
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <div className="p-3 sm:p-4 bg-white dark:bg-slate-900 border-t border-slate-100 dark:border-slate-800">
            {chatMessages.length === 0 && (
              <div className="mb-3">
                {/* Desktop: native select */}
                <div className="relative hidden md:block">
                  <select
                    value={category}
                    onChange={(e) => {
                      setCategory(e.target.value);
                      setContextCategory(e.target.value);
                    }}
                    className="w-full p-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-black uppercase tracking-widest text-slate-700 dark:text-slate-200 focus:ring-2 focus:ring-[#0f7699] dark:focus:ring-[#4fc3f7] outline-none transition-all appearance-none pr-10"
                  >
                    {categoryOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500 pointer-events-none" size={16} />
                </div>
                {/* Mobile: touch button opens bottom sheet */}
                <button
                  onClick={() => setShowCategorySheet(true)}
                  className="md:hidden w-full p-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-black uppercase tracking-widest text-slate-700 dark:text-slate-200 flex items-center justify-between active:scale-[0.98] transition-transform"
                >
                  <span>{categoryOptions.find(o => o.value === category)?.label || category}</span>
                  <ChevronDown size={16} className="text-slate-400 dark:text-slate-500" />
                </button>
              </div>
            )}
            {isAdmin && isSupportOnline && (
              <div className="mb-3">
                <button
                  onClick={() => handleEndChat(true)}
                  className="w-full py-2 bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-slate-800 dark:hover:bg-slate-200 transition-colors flex items-center justify-center gap-2"
                >
                  <CheckCircle size={14} />
                  {t("helpChat.endChatAndCloseTicket")}
                </button>
              </div>
            )}

            <div className="flex gap-2.5 items-end">
              <textarea
                placeholder={t("helpChat.typeMessage")}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                rows={1}
                className="flex-1 p-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl text-sm font-medium text-slate-700 dark:text-slate-200 placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:ring-2 focus:ring-[#0f7699] dark:focus:ring-[#4fc3f7] outline-none transition-all resize-none max-h-32"
              />
              <button
                onClick={handleSendMessage}
                disabled={!message.trim()}
                className={`w-11 h-11 sm:w-12 sm:h-12 rounded-2xl flex items-center justify-center transition-all shrink-0 ${
                  message.trim()
                    ? "bg-[#0f7699] dark:bg-[#4fc3f7] text-white dark:text-slate-900 shadow-[0_4px_12px_rgba(15,118,153,0.3)] hover:bg-[#0a5975] dark:hover:bg-[#81d4fa] active:scale-95"
                    : "bg-slate-100 dark:bg-slate-800 text-slate-300 dark:text-slate-600 cursor-not-allowed"
                }`}
              >
                <Send size={18} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Floating Action Button - hidden on mobile (shown in Header instead) */}
      {!open && (
        <div className="fixed bottom-8 right-8 z-[9998] hidden sm:block">
          <button
            onClick={() => setOpen(true)}
            className="w-16 h-16 rounded-full bg-gradient-to-br from-[#0f7699] to-[#4c9fb9] text-white shadow-[0_8px_32px_rgba(15,118,153,0.4)] flex items-center justify-center transition-all duration-500 hover:scale-110 hover:-rotate-6 hover:shadow-[0_12px_40px_rgba(15,118,153,0.5)] active:scale-95 group relative"
            title={t("helpChat.needHelp")}
          >
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 flex h-5 w-5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-5 w-5 bg-red-500 border-2 border-white text-[10px] font-black items-center justify-center">
                  {unreadCount}
                </span>
              </span>
            )}
            <MessagesSquare size={28} />
          </button>
        </div>
      )}

      {/* Mobile category bottom sheet */}
      {showCategorySheet && (
        <div className="fixed inset-0 z-[9999] md:hidden" onClick={() => setShowCategorySheet(false)}>
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm animate-in fade-in duration-200" />
          <div className="absolute bottom-0 left-0 right-0 bg-white dark:bg-slate-900 rounded-t-3xl shadow-2xl animate-in slide-in-from-bottom duration-300" onClick={e => e.stopPropagation()}>
            <div className="flex justify-center pt-3 pb-1">
              <div className="w-10 h-1 bg-slate-300 dark:bg-slate-700 rounded-full" />
            </div>
            <div className="px-5 pb-3 pt-1">
              <h3 className="text-base font-bold text-slate-800 dark:text-slate-100">{t("helpChat.selectCategory")}</h3>
            </div>
            <div className="px-3 pb-8">
              {categoryOptions.map((opt) => {
                const isSelected = category === opt.value;
                return (
                  <button
                    key={opt.value}
                    onClick={() => {
                      setCategory(opt.value);
                      setContextCategory(opt.value);
                      setShowCategorySheet(false);
                    }}
                    className={`w-full flex items-center justify-between px-4 py-4 rounded-2xl mb-1 transition-all active:scale-[0.98] ${
                      isSelected
                        ? "bg-[#0f7699]/10 dark:bg-[#4fc3f7]/15 text-[#0f7699] dark:text-[#4fc3f7]"
                        : "text-slate-700 dark:text-slate-300"
                    }`}
                  >
                    <span className="text-sm font-bold">{opt.label}</span>
                    {isSelected && <CheckCircle size={18} className="text-[#0f7699] dark:text-[#4fc3f7]" />}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <style>
        {`
          @keyframes bounce {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-4px); }
          }
        `}
      </style>
    </>
  );
};

export default HelpChatButton;
